





















































































































































































































































































import {
  computed,
  defineComponent,
  ref,
  useRoute
} from '~/utils/nuxt3-migration'
import RentalPeriod from '~/components/car/classifieds/view/partials/rental/RentalPeriod.vue'
import CCard from '~/components/shared/configurable/card/CCard.vue'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { ClassifiedExtraRentalService, Payment } from '~/models/rentals/types'
import { faCheck, faMapMarker } from '@fortawesome/free-solid-svg-icons'
import { capitalizeString } from '~/utils/string'
import CGoogleMapEmbed from '~/components/shared/configurable/map/CGoogleMapEmbed.vue'
import DirectionsButton from '~/components/car/maps/DirectionsButton.vue'
import { useI18n } from '~/compositions/i18n'
import CTable from '~/components/shared/configurable/table/CTable.vue'
import ContactWithHistory from '~/components/car/classifieds/view/partials/ContactWithHistory.vue'
import ContactPrompt from '~/components/car/classifieds/view/partials/ContactPrompt.vue'
import ExternalLink from '~/components/car/classifieds/view/partials/ExternalLink.vue'
import CTableHead from '~/components/shared/configurable/table/CTableHead.vue'
import CTableRow from '~/components/shared/configurable/table/CTableRow.vue'
import CTableHeadCell from '~/components/shared/configurable/table/CTableHeadCell.vue'
import CTableBody from '~/components/shared/configurable/table/CTableBody.vue'
import CTableDataCell from '~/components/shared/configurable/table/CTableDataCell.vue'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { Option } from '~/models/shared/types'

export default defineComponent({
  components: {
    CCustomSelect,
    CTableDataCell,
    CTableBody,
    CTableHeadCell,
    CTableRow,
    CTableHead,
    ContactPrompt,
    CCard,
    RentalPeriod,
    CGoogleMapEmbed,
    DirectionsButton,
    CTable,
    ContactWithHistory,
    ExternalLink
  },
  setup() {
    const { state, getters } = useNamespacedStore<ClassifiedViewState>(
      CLASSIFIED_VIEW_NS
    )

    const selectedPaidServices = ref([])
    const mapModalIsShown = ref(false)
    const availabilityModalIsShown = ref(false)
    const selectedPickupLocation = ref(null)

    const { t } = useI18n()
    const route = useRoute()

    const rentalInfo = computed(() => getters('rentalInfo'))
    const rentalInsurance = computed(() => state.classified?.rentalInsurance)
    const pricePerMonth = computed(() => state.classified?.pricePerMonth)
    const rentalPickupLocation = computed(
      () => state.classified?.rentalPickupLocation
    )
    const classifiedId = computed(() => state.classified?.id)
    const externalLink = computed(() => state.classified?.external_link)

    const extraRentalServices = computed(
      () => state.classified?.extraRentalServices
    )
    const freeExtraRentalServices = computed(() =>
      extraRentalServices.value?.filter(
        s =>
          s.payment.value === Payment.FREE &&
          !s.value.startsWith('delivery_to_')
      )
    )
    const paidExtraRentalServices = computed(() =>
      extraRentalServices.value?.filter(
        s =>
          s.payment.value !== Payment.FREE &&
          !s.value.startsWith('delivery_to_')
      )
    )
    const pickupLocationRentalServices = computed(() =>
      extraRentalServices.value?.filter(s => s.value.startsWith('delivery_to_'))
    )
    const isAvailableForDays = computed(
      () => rentalInfo.value.isAvailableForDays
    )
    const minimumRentalPeriod = computed(
      () => rentalInfo.value.minimumRentalPeriod
    )
    const baseInsuranceOptions = computed(
      () => rentalInsurance.value?.baseInsurancePackage || []
    )
    const basicInsuranceOptionsTooltip = computed(() =>
      baseInsuranceOptions.value.map(o => o.name).join(', ')
    )
    const hasMixedInsuranceOption = computed(
      () => rentalInsurance.value?.hasMixedInsuranceOption
    )

    const hasDiscount = computed(
      () =>
        priceAfterDiscount.value &&
        priceAfterDiscount.value !== displayPrice.value
    )

    const seller = computed(() => state.seller)
    const showContact = computed(() => state.showContact)
    const showContactPrompt = computed(() => state.showContactPrompt)
    const hasSelectedDateRange = computed(
      () =>
        route.value.query['rental_period-from'] ||
        route.value.query['rental_period-to']
    )

    const priceDiscounts = computed(() => {
      const discountsPayload = state.classified?.price_discounts
      let counter = 0
      const formattedPayload = discountsPayload?.reduce((acc, cur) => {
        acc.push({
          from: cur.days,
          to:
            discountsPayload.length > counter + 1
              ? discountsPayload[counter + 1].days - 1
              : null,
          discount: cur.discount
        })
        counter++
        return acc
      }, [] as any[])

      return formattedPayload
    })

    const icons = {
      check: faCheck,
      marker: faMapMarker
    }

    function getPickupLocationServicePrice(
      service: ClassifiedExtraRentalService
    ) {
      switch (service.payment.value) {
        case Payment.FREE: {
          return t('free::rental_extra_service')
        }
        case Payment.ONE_TIME: {
          return `${service.price} €`
        }
        default: {
          return ''
        }
      }
    }

    function getPaidServiceSuffix(service: ClassifiedExtraRentalService) {
      switch (service.payment.value) {
        case Payment.ONE_TIME: {
          return t('once')
            .toString()
            .toLowerCase()
        }
        case Payment.DAILY: {
          return `/ ${t('day::single')}`.toLowerCase()
        }
      }
      return ''
    }

    const priceAfterDiscount = computed(() => {
      const priceAfterDiscount = rentalInfo.value.priceAfterDiscount
      return priceAfterDiscount && calcPriceWithServices(priceAfterDiscount)
    })
    const displayPrice = computed(() =>
      calcPriceWithServices(rentalInfo.value.priceForPeriod)
    )

    const calcPriceWithServices = (price: number = 0): number => {
      let priceOfServices = price
      if (selectedPaidServices.value.length && paidExtraRentalServices.value) {
        for (const paidServiceValue of selectedPaidServices.value) {
          const paidService = paidExtraRentalServices.value.find(
            s => s.value === paidServiceValue
          )
          if (paidService) {
            switch (paidService.payment.value) {
              case Payment.ONE_TIME: {
                priceOfServices += paidService.price
                break
              }
              case Payment.DAILY: {
                priceOfServices +=
                  paidService.price * rentalInfo.value.totalDays
                break
              }
            }
          }
        }
      }
      if (selectedPickupLocation.value) {
        const pickupLocation = pickupLocationRentalServices.value?.find(
          p => p.value === selectedPickupLocation.value
        )
        if (pickupLocation) {
          priceOfServices += pickupLocation.price
        }
      }
      return priceOfServices
    }

    function showMapModal() {
      mapModalIsShown.value = true
    }

    function showAvailabilityModal() {
      availabilityModalIsShown.value = true
    }

    const contactExtraText = computed(() => {
      function getDateStr() {
        return `Ημερομηνία: ${route.value.query['rental_period-from'] ||
          '-'} έως ${route.value.query['rental_period-to'] || '-'}`
      }
      function getPaidExtrasStr() {
        const selectedPaidServicesNames = (
          selectedPaidServices.value || []
        ).map(s => {
          if (s === 'mixed_insurance') {
            return 'Μικτή ασφάλεια'
          }
          return paidExtraRentalServices.value!.find(ps => ps.value === s)!.name
        })
        return `Extras: ${selectedPaidServicesNames.join(', ') || '-'}`
      }
      function getPriceStr() {
        let price = displayPrice.value
        if (hasDiscount) {
          price = priceAfterDiscount.value
        }
        return `Τιμή: ${price || '-'}€`
      }
      function getPickupLocationStr() {
        if (selectedPickupLocation.value) {
          const pickupLocation = pickupLocationRentalServices.value?.find(
            p => p.value === selectedPickupLocation.value
          )
          if (pickupLocation) {
            return `Σημείο παραλαβής: ${pickupLocation.name}`
          }
        }
        return ''
      }
      return `\n${getDateStr()}\n${getPaidExtrasStr()}\n${getPriceStr()}\n${getPickupLocationStr()}`
    })

    const pickupLocationRentalServicesOptions = computed<Option[]>(() => [
      {
        value: null,
        text: t('pickup location').toString()
      },
      ...(pickupLocationRentalServices.value ?? []).map(s => ({
        value: s.value,
        text: `${s.name} (${getPickupLocationServicePrice(s)})`
      }))
    ])

    return {
      rentalInfo,
      extraRentalServices,
      freeExtraRentalServices,
      paidExtraRentalServices,
      getPaidServiceSuffix,
      icons,
      selectedPaidServices,
      displayPrice,
      priceAfterDiscount,
      hasDiscount,
      capitalizeString,
      isAvailableForDays,
      minimumRentalPeriod,
      baseInsuranceOptions,
      rentalInsurance,
      rentalPickupLocation,
      basicInsuranceOptionsTooltip,
      hasMixedInsuranceOption,
      mapModalIsShown,
      showMapModal,
      contactExtraText,
      availabilityModalIsShown,
      showAvailabilityModal,
      classifiedId,
      seller,
      showContact,
      showContactPrompt,
      hasSelectedDateRange,
      externalLink,
      pricePerMonth,
      pickupLocationRentalServices,
      selectedPickupLocation,
      pickupLocationRentalServicesOptions,
      priceDiscounts
    }
  }
})
